import React, { useEffect, useRef } from 'react';
import { Box, GlobalStyles } from '@mui/material';
import useMediaQuery from '@mui/material/useMediaQuery';

const SIZE = 18;

const CustomCursor = () => {
  const isTouch = useMediaQuery('(hover: none)');
  const cursorRef = useRef(null);
  const visibleRef = useRef(false);

  useEffect(() => {
    if (isTouch) return;
    const el = cursorRef.current;
    if (!el) return;

    let raf = 0;
    let targetX = 0;
    let targetY = 0;

    const hide = () => {
      if (visibleRef.current) {
        el.style.opacity = '0';
        visibleRef.current = false;
      }
    };

    const show = () => {
      if (!visibleRef.current) {
        el.style.opacity = '1';
        visibleRef.current = true;
      }
    };

    const isOverIframe = (x, y) => {
      const under = document.elementFromPoint(x, y);
      return !!(under && under.tagName === 'IFRAME');
    };

    const onMove = (e) => {
      // Once the pointer is over an iframe, the parent stops getting mousemove
      // events — but the LAST event before that has the iframe as the target,
      // so we can detect entry and hide.
      if (e.target && e.target.tagName === 'IFRAME') {
        hide();
        return;
      }
      if (isOverIframe(e.clientX, e.clientY)) {
        hide();
        return;
      }
      targetX = e.clientX;
      targetY = e.clientY;
      show();
      if (!raf) {
        raf = requestAnimationFrame(() => {
          el.style.transform = `translate(${targetX}px, ${targetY}px)`;
          raf = 0;
        });
      }
    };

    const onOver = (e) => {
      if (e.target && e.target.tagName === 'IFRAME') hide();
    };

    // When an iframe takes focus, the parent window blurs — hide the cursor
    // because we won't get any more events until focus returns.
    const onBlur = () => hide();

    // Periodic safety net: while the mouse is "lost" inside an iframe, we
    // can't track it, so just keep the cursor hidden as long as the parent
    // window doesn't have focus or the focused element is an iframe.
    const tick = setInterval(() => {
      const active = document.activeElement;
      if (!document.hasFocus() || (active && active.tagName === 'IFRAME')) {
        hide();
      }
    }, 250);

    window.addEventListener('mousemove', onMove);
    document.addEventListener('mouseleave', hide);
    document.addEventListener('mouseover', onOver);
    window.addEventListener('blur', onBlur);

    return () => {
      window.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseleave', hide);
      document.removeEventListener('mouseover', onOver);
      window.removeEventListener('blur', onBlur);
      clearInterval(tick);
      if (raf) cancelAnimationFrame(raf);
    };
  }, [isTouch]);

  if (isTouch) return null;

  return (
    <>
      <GlobalStyles
        styles={{
          'html, body, *': { cursor: 'none !important' },
        }}
      />
      <Box
        ref={cursorRef}
        sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: `${SIZE}px`,
          height: `${SIZE}px`,
          pointerEvents: 'none',
          zIndex: 9999,
          opacity: 0,
          transition: 'opacity 0.15s ease',
          willChange: 'transform',
        }}
      >
        {/* Solid black fill, clipped to the triangle. */}
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            clipPath: 'polygon(0 0, 0 100%, 60% 80%, 100% 100%)',
            backgroundColor: '#000',
          }}
        />
        {/* White outline. */}
        <svg
          viewBox="0 0 100 100"
          width={SIZE}
          height={SIZE}
          style={{
            position: 'absolute',
            inset: 0,
            overflow: 'visible',
            filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.15))',
          }}
        >
          <polygon
            points="0,0 0,100 60,80 100,100"
            fill="none"
            stroke="rgba(255,255,255,0.9)"
            strokeWidth="5"
            strokeLinejoin="round"
          />
        </svg>
      </Box>
    </>
  );
};

export default CustomCursor;
